import { AfterViewInit, Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { Store } from '@ngxs/store';
import { APP_CONFIG, IAppSettings } from '@nha/config/interfaces/iapp-settings.interface';
import { Subscription } from 'rxjs';
import { GMapsCustomPopup } from '../../helpers/gmaps-custom-popup-helper';
import { RootState } from '../../store/root.state';
import { IApplicationContent, INearbySchool } from '../../types';
import { NearbySchoolsActions } from '../nearby-schools.actions';
import { NearbySchoolsState } from '../nearby-schools.state';

@Component({
	selector: 'app-nearby-schools-map',
	standalone: true,
	imports: [GoogleMapsModule],
	templateUrl: './nearby-schools-map.component.html',
	styleUrls: ['./nearby-schools-map.component.scss']
})
export class NearbySchoolsMapComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
	@Input() nearbySchools: AbstractControl[];
	@Input() guardianHouseholdLatLng: google.maps.LatLngLiteral;
	@Output() addedToApplication = new EventEmitter<number>();

	@ViewChild(GoogleMap) mapComp: GoogleMap;

	appSettings = inject<IAppSettings>(APP_CONFIG);
	store = inject(Store);

	schools: INearbySchool[] = [];
	selectedSchoolID: number | null = null;
	map: google.maps.Map;
	mapMarkers: InstanceType<google.maps.MarkerLibrary['AdvancedMarkerElement']>[] = [];
	currentPopup: GMapsCustomPopup;
	currentPopupEventSub: Subscription;
	selectedSchoolSub: Subscription;
	formChangeListenerSubs: Subscription[] = [];
	appContent: IApplicationContent;

	mapOptions: google.maps.MapOptions = {
		fullscreenControl: false,
		streetViewControl: false,
		mapTypeControl: false,
		mapId: 'DEMO_MAP_ID'
	};

	ngOnInit() {
		this.schools = this.nearbySchools.map((sc) => sc.value);
		this.appContent = this.store.selectSnapshot(RootState.content);

		this.selectedSchoolSub = this.store.select(NearbySchoolsState.selectedSchoolID).subscribe((id) => {
			this.selectedSchoolID = id;
			this.updateMarkers();
			if (id) this.panToSchool(id);
		});
	}

	ngAfterViewInit() {
		this.map = this.mapComp.googleMap!;
		this.initializeMarkers();
	}

	ngOnChanges() {
		this.schools = this.nearbySchools.map((sc) => sc.value);
		this.cleanupFormSubs();
		this.bindFormSubs();
		this.updateMarkers();
	}

	ngOnDestroy() {
		this.selectedSchoolSub?.unsubscribe();
		this.currentPopupEventSub?.unsubscribe();
		this.cleanupFormSubs();
	}

	private cleanupFormSubs() {
		this.formChangeListenerSubs.forEach((s) => s.unsubscribe());
		this.formChangeListenerSubs = [];
	}

	private bindFormSubs() {
		this.formChangeListenerSubs = this.nearbySchools.map((ns) => {
			const ctrls = (ns as UntypedFormGroup).controls;
			return ctrls.isApplying.valueChanges.subscribe((isApplying) => {
				this.schools.find((s) => s.schoolID === ctrls.schoolID.value)!.isApplying = isApplying;
				this.updateMarkers();
			});
		});
	}

	async initializeMarkers() {
		const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;
		this.createMarkers(AdvancedMarkerElement);
	}

	private createMarkers(AdvancedMarkerElement: typeof google.maps.marker.AdvancedMarkerElement) {
		this.mapMarkers.forEach((m) => (m.map = null));
		this.mapMarkers = [];

		// Guardian marker
		this.mapMarkers.push(
			new AdvancedMarkerElement({
				map: this.map,
				position: this.guardianHouseholdLatLng,
				title: 'Guardian Household',
				content: this.makeIcon(`${window['applicationAppSettingPathOverride'] ?? ''}assets/images/map-marker-gray.png`)
			})
		);

		// School markers
		this.schools.forEach((school) => {
			if (!school.latitude || !school.longitude) return;
			const marker = new AdvancedMarkerElement({
				map: this.map,
				position: { lat: school.latitude, lng: school.longitude },
				title: school.schoolName,
				content: this.makeIcon(`${window['applicationAppSettingPathOverride'] ?? ''}assets/images/map-marker-orange.png`)
			});
			marker.addListener('click', () =>
				this.store.dispatch(new NearbySchoolsActions.SelectNearbySchool(this.selectedSchoolID === school.schoolID ? null : school.schoolID))
			);
			this.mapMarkers.push(marker);
		});

		this.updateMarkers();
		this.fitBounds();
	}

	private updateMarkers() {
		const appliedNames = this.schools.filter((s) => s.isApplying).map((s) => s.schoolName);

		this.mapMarkers.forEach((marker) => {
			const iconUrl = appliedNames.includes(marker.title!) ? 'map-marker-green.png' : 'map-marker-orange.png';
			if (marker.title === 'Guardian Household') return;
			marker.content = this.makeIcon(`${window['applicationAppSettingPathOverride'] ?? ''}/assets/images/${iconUrl}`);
		});

		const selMarker = this.mapMarkers.find((m) => m.title === this.schools.find((s) => s.schoolID === this.selectedSchoolID)?.schoolName);
		if (selMarker) {
			selMarker.content = this.makeIcon(`${window['applicationAppSettingPathOverride'] ?? ''}/assets/images/map-marker-blue.png`);
			this.showPopup(selMarker);
		} else {
			this.currentPopup?.onRemove();
		}
	}

	private fitBounds() {
		const bounds = new google.maps.LatLngBounds();
		bounds.extend(this.guardianHouseholdLatLng);
		this.mapMarkers.forEach((m) => bounds.extend(m.position!));
		this.map.fitBounds(bounds);
	}

	private showPopup(marker: InstanceType<google.maps.MarkerLibrary['AdvancedMarkerElement']>) {
		this.currentPopup?.onRemove();

		const school = this.schools.find((s) => s.schoolName === marker.title)!;
		let content = `<button class="popup-close-btn"><span class="fa fa-close"></span></button>
      <h6>${school.schoolName}</h6>
      <small>${school.address}, ${school.city}</small>`;
		content += school.isApplying
			? `<h5><span class="fa fa-check"></span> ${this.appContent.resourceStrings['nha.sa.applyingconfirmationcheckmark']}</h5>`
			: `<div class="nha-text-center">
           <button class="nha-btn nha-btn-secondary add-btn">
             <span class="fa fa-plus-circle"></span>
             ${this.appContent.resourceStrings['nha.sa.addtoapplyadditionalschool']}
           </button>
         </div>`;

		this.currentPopup = new GMapsCustomPopup(marker.position!, content);
		this.currentPopup.setMap(this.map);

		this.currentPopupEventSub?.unsubscribe();
		this.currentPopupEventSub = this.currentPopup.getMouseEvents().subscribe((e) => {
			const tgt = e.target as HTMLElement;
			if (tgt.classList.contains('add-btn')) this.addedToApplication.emit(this.selectedSchoolID!);
			if (tgt.classList.contains('popup-close-btn') || tgt.classList.contains('fa-close')) {
				this.store.dispatch(new NearbySchoolsActions.SelectNearbySchool(null));
				this.currentPopup.onRemove();
			}
		});
	}

	private panToSchool(id: number) {
		const mk = this.mapMarkers.find((m) => m.title === this.schools.find((s) => s.schoolID === id)?.schoolName);
		if (mk) this.map.panTo(mk.position!);
	}

	private makeIcon(url: string): HTMLElement {
		const img = document.createElement('img');
		img.src = url;
		img.style.width = '28px';
		img.style.height = '40px';
		return img;
	}
}
